/**
 * Светящаяся полоса прогресса: градиент бирюза → янтарь, мягкий ореол и
 * бегущий блик. Ширина догоняет значение пружиной, поэтому каждый шаг
 * вперёд виден как движение, а не как скачок.
 */

import { motion } from 'framer-motion';

interface GlowProgressProps {
  readonly value: number;
  readonly max: number;
  /** Подпись слева над полосой; справа — крупный счёт. */
  readonly label?: string;
  readonly size?: 'md' | 'lg';
  readonly className?: string;
}

export function GlowProgress({ value, max, label, size = 'md', className }: GlowProgressProps) {
  const safeMax = max > 0 ? max : 1;
  const percent = (Math.min(Math.max(value, 0), safeMax) / safeMax) * 100;
  const height = size === 'lg' ? 'h-4' : 'h-2.5';

  return (
    <div className={className}>
      {label !== undefined && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold text-muted">{label}</span>
          <span className="font-heading text-lg font-extrabold tabular-nums text-heading">
            {Math.min(value, max)}
            <span className="text-sm font-bold text-muted">/{max}</span>
          </span>
        </div>
      )}
      <div
        className={`relative w-full overflow-hidden rounded-full bg-heading/10 ${height}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.min(value, max)}
        aria-label={label}
      >
        <motion.div
          className="glow-shimmer relative h-full overflow-hidden rounded-full"
          style={{
            background: 'var(--grad-progress)',
            boxShadow: '0 0 12px rgb(var(--c-teal)/0.55), 0 0 26px rgb(var(--c-accent)/0.35)',
          }}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ type: 'spring', stiffness: 90, damping: 18 }}
        />
      </div>
    </div>
  );
}
